import type { TranslationDictionary } from './it';

export const en: TranslationDictionary = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    confirm: 'Confirm',
    loading: 'Loading...',
    error: 'Error',
    success: 'Operation completed',
    back: 'Back',
    next: 'Next',
    close: 'Close',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    edit: 'Edit',
    add: 'Add',
    remove: 'Remove',
    yes: 'Yes',
    no: 'No',
    or: 'or',
    and: 'and',
    all: 'All',
    none: 'None',
    required: 'Required',
    optional: 'Optional',
    actions: 'Actions',
    details: 'Details',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    enabled: 'Enabled',
    disabled: 'Disabled',
    from: 'From',
    to: 'To',
    date: 'Date',
    price: 'Price',
    total: 'Total',
    noResults: 'No results',
    retry: 'Try again',
    tryAgain: 'Try again',
    learnMore: 'Learn more',
    showMore: 'Show more',
    showLess: 'Show less',
  },
  nav: {
    dashboard: 'Dashboard',
    monitors: 'Monitors',
    notifications: 'Notifications',
    subscription: 'Subscription',
    settings: 'Settings',
  },
  auth: {
    login: 'Log in',
    register: 'Sign up',
    logout: 'Log out',
    forgotPassword: 'Forgot password?',
    resetPassword: 'Reset password',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    emailVerification: 'Email verification',
    emailVerificationSent: 'Verification email sent. Check your inbox.',
    emailVerified: 'Email verified successfully!',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    nameLabel: 'Name',
    loginTitle: 'Log in to your account',
    registerTitle: 'Create your account',
    loginSubtitle: 'Welcome to FlyMind',
    registerSubtitle: 'Start monitoring flight prices',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    resetPasswordTitle: 'Reset your password',
    resetPasswordSubtitle: 'Enter your email to receive a reset link',
    resetPasswordSent: 'If the email is registered, you will receive a link to reset your password.',
    resetPasswordSuccess: 'Password reset successfully. You can now log in.',
    passwordRequirements: 'At least 8 characters, one uppercase letter and one number',
    termsAgreement: 'By signing up you agree to our Terms of Service and Privacy Policy',
    socialLogin: 'Or log in with',
    googleLogin: 'Continue with Google',
    continueWithGoogle: 'Continue with Google',
    continueWithApple: 'Continue with Apple',
    facebookComingSoon: 'Facebook — Coming Soon',
    backToHome: 'Back to home',
    backToLogin: 'Back to login',
    sendResetLink: 'Send reset link',
    enterNewPassword: 'Enter your new password',
    tokenMissing: 'Token missing. Request a new password reset link.',
    emailPlaceholder: 'name@email.com',
    passwordPlaceholder: 'Your password',
    namePlaceholder: 'Your name',
    minPasswordPlaceholder: 'At least 8 characters',
    repeatPasswordPlaceholder: 'Repeat password',
    passwordsDontMatch: 'Passwords do not match',
    mustAcceptTerms: 'You must accept the Terms of Service',
    socialLoginError: 'Social login error',
    ssoFailedRetry: 'Social login failed. Please try again.',
    inAppBrowserWarning: 'You are using an in-app browser. To log in reliably, open this page in Safari or Chrome.',
    privateModeWarning: 'Your browser is blocking persistent storage (likely private/incognito mode). You may be logged out on reload.',
    invitedByFriend: 'Invited by a friend',
    pwStrengthWeak: 'Weak',
    pwStrengthMedium: 'Medium',
    pwStrengthStrong: 'Strong',
    pwReqLength: 'At least 8 characters',
    pwReqUppercase: 'One uppercase letter',
    pwReqNumber: 'One number',
  },
  dashboard: {
    title: 'Dashboard',
    welcome: 'Welcome back',
    noMonitors: "You don't have any active monitors yet.",
    noMonitorsAction: 'Create your first monitor to start saving!',
    addMonitor: 'New monitor',
    activeMonitors: 'Active monitors',
    totalSavings: 'Estimated total savings',
    bestDeal: 'Best deal',
    recentAlerts: 'Recent alerts',
    priceHistory: 'Price history',
    lastUpdate: 'Last update',
    overview: 'Overview',
    statistics: 'Statistics',
    noAlertsYet: 'No alerts yet. We are monitoring your flights!',
    yourMonitors: 'Your monitors',
    seeAll: 'See all',
    voiceSearchAria: 'Voice search',
    bookNow: 'Book now',
    fallbackUsername: 'User',
    checkoutSuccess: 'Subscription activated! Welcome.',
    checkoutCancelled: 'Checkout cancelled. You can try again anytime.',
    inviteFriendsProgress: 'Invite friends: {progress}/5 to unlock 5 free months',
    shareYourLink: 'Share your link',
    timeJustNow: 'just now',
    timeMinAgo: 'min ago',
    timeHourAgo: 'hour',
    timeHoursAgo: 'hours',
    timeAgoSuffix: 'ago',
    timeDayAgo: 'day',
    timeDaysAgo: 'days',
  },
  monitor: {
    create: 'Create monitor',
    edit: 'Edit monitor',
    delete: 'Delete monitor',
    deleteConfirm: 'Are you sure you want to delete this monitor? This action cannot be undone.',
    departure: 'Departure',
    destination: 'Destination',
    departureAirport: 'Departure airport',
    arrivalAirport: 'Arrival airport',
    dates: 'Dates',
    departureDate: 'Departure date',
    returnDate: 'Return date',
    budget: 'Maximum budget',
    budgetOptional: 'Maximum budget (optional)',
    status: 'Status',
    oneWay: 'One way',
    roundTrip: 'Round trip',
    currentPrice: 'Current price',
    lowestPrice: 'Lowest price',
    averagePrice: 'Average price',
    priceChange: 'Change',
    activate: 'Activate',
    deactivate: 'Deactivate',
    activated: 'Monitor activated',
    deactivated: 'Monitor deactivated',
    created: 'Monitor created successfully',
    updated: 'Monitor updated',
    deleted: 'Monitor deleted',
    limitReached: 'You have reached the monitor limit for your plan.',
    upgradeForMore: 'Upgrade to a higher plan to add more monitors.',
    searchAirport: 'Search airport...',
    swapAirports: 'Swap airports',
    noReturnDate: 'No return date',
  },
  monitorCard: {
    trendStable: 'stable',
    trendUp: 'rising',
    trendDown: 'falling',
  },
  monitors: {
    title: 'My monitors',
    newMonitor: 'New monitor',
    totalCount: 'total monitors',
    totalCountSingular: 'total monitor',
    alertStatus: 'Alert status',
    status: 'Status',
    type: 'Type',
    allFilter: 'All',
    activeFilter: 'Active',
    inactiveFilter: 'Inactive',
    fixedDates: 'Fixed dates',
    flexiblePeriod: 'Flexible period',
    searchRoute: 'Search by route',
    sortBy: 'Sort by',
    sortRecent: 'Most recent',
    sortPriceAsc: 'Lowest price',
    sortAlertStatus: 'Alert status',
    emptyTitle: 'You have no monitors yet',
    emptyDescription: 'Create your first monitor to start saving on flights.',
    emptyAction: 'Create your first monitor',
    noResults: 'No monitors found',
    noResultsDescription: 'Try adjusting your filters to see more results.',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {page} of {total}',
    book: 'Book',
    budget: 'Budget',
    period: 'Period',
    flexible: 'Flexible',
    premiumEco: 'Premium Eco',
    goodAlert: 'Good',
    rareAlert: 'Rare',
    exceptionalAlert: 'Exceptional',
    searchPlaceholder: 'Ex. FCO, MXP, CDG...',
    voiceSearchAria: 'Voice search',
  },
  alerts: {
    good: 'Good price',
    rare: 'Rare price',
    exceptional: 'Exceptional price',
    none: 'No alerts',
    goodDescription: 'The price has dropped 10-20% below average. A good time to book.',
    rareDescription: 'The price has dropped 20-35% below average. A rare opportunity!',
    exceptionalDescription: 'The price has dropped more than 35% below average. An exceptional deal, book now!',
    priceDrop: 'Price has dropped',
    below: 'below average',
    alertTriggered: 'Alert triggered',
    viewDetails: 'View details',
  },
  notifications: {
    title: 'Notifications',
    markRead: 'Mark as read',
    markAllRead: 'Mark all as read',
    empty: 'You have no notifications',
    emptyDescription: 'Notifications about your flight prices will appear here.',
    newNotification: 'New notification',
    settings: 'Notification settings',
    emailNotifications: 'Email notifications',
    pushNotifications: 'Push notifications',
    inAppNotifications: 'In-app notifications',
    unread: 'Unread',
    allNotifications: 'All',
    deleteAll: 'Delete all',
    deleteAllConfirm: 'Are you sure you want to delete all notifications?',
    newSingular: 'new',
    newPlural: 'new',
    bookNow: 'Book now',
    loadMore: 'Load more notifications',
  },
  subscription: {
    free: 'Free',
    premium: 'Pro',
    unlimited: 'Unlimited',
    upgrade: 'Upgrade to',
    downgrade: 'Downgrade to',
    currentPlan: 'Current plan',
    changePlan: 'Change plan',
    cancelSubscription: 'Cancel subscription',
    cancelConfirm: 'Are you sure you want to cancel your subscription? You will retain access until the end of the billing period.',
    cancelSuccess: 'Subscription cancelled. You will have access until the end of the period.',
    reactivate: 'Reactivate subscription',
    reactivateSuccess: 'Subscription reactivated successfully!',
    freeFeatures: {
      monitor: '1 monitor',
      alerts: 'Basic alerts',
      email: 'Email notifications',
    },
    premiumFeatures: {
      monitors: '5 monitors',
      alerts: 'Advanced alerts',
      ai: 'AI analysis',
      predictive: 'Predictive alerts',
      priority: 'Priority support',
    },
    unlimitedFeatures: {
      monitors: '15 monitors',
      alerts: 'Advanced alerts',
      ai: 'Advanced AI analysis',
      predictive: 'Predictive alerts',
      priority: 'Priority support',
      api: 'API access',
    },
    billingHistory: 'Billing history',
    nextBilling: 'Next billing',
    paymentMethod: 'Payment method',
    updatePayment: 'Update payment method',
    pageTitle: 'Subscription',
    priceFree: 'Free',
    perMonth: 'mo',
    perYear: 'yr',
    perMonthShort: 'mo',
    perYearShort: 'yr',
    monthly: 'Monthly',
    yearly: 'Annual',
    recommended: 'Recommended',
    downgradeShort: 'Downgrade',
    manageSubscription: 'Manage subscription',
    unlimitedSentinels: 'Unlimited sentinels',
    sentinelMaxPrefix: 'Max',
    sentinelSuffix: 'sentinel(s)',
    aiBadge: 'AI',
    voiceBadge: 'Voice',
    checkoutError: 'Checkout error',
    portalError: 'Error opening portal',
    upgradeBuy: 'Upgrade to',
    sentinelOne: '1 sentinel',
    sentinelFive: '5 sentinels',
    sentinelUnlimited: 'Unlimited sentinels',
    notifEmailPush: 'Email & push notifications',
    historyPrices: 'Price history',
    predictiveAlerts: 'Predictive alerts',
    aiCompile: 'AI-powered search',
    voiceAgentFeature: 'AI voice agent',
    faqQ1: 'Can I change plans at any time?',
    faqA1: 'Yes, you can upgrade or downgrade your plan at any time. The change takes effect immediately and costs are prorated.',
    faqQ2: 'How does cancellation work?',
    faqA2: 'You can cancel your subscription at any time from the Stripe management portal. You will keep access to your plan features until the end of the current billing period.',
    faqQ3: 'What happens to my monitors if I cancel?',
    faqA3: 'If you have more monitors than the free plan limit, they will be paused automatically. You can choose which ones to keep active.',
    faqQ4: 'What is the difference between monthly and annual?',
    faqA4: 'With the annual plan you save 25% (3 months free). Pro: €8.99/year. Unlimited: €44.99/year.',
  },
  settings: {
    title: 'Settings',
    profile: 'Profile',
    notifications: 'Notifications',
    privacy: 'Privacy',
    deleteAccount: 'Delete account',
    deleteAccountConfirm: 'Are you sure you want to delete your account? All your data will be permanently deleted.',
    deleteAccountWarning: 'This action cannot be undone.',
    accountDeleted: 'Account deleted successfully.',
    profileUpdated: 'Profile updated successfully.',
    language: 'Language',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    exportData: 'Export your data',
    exportDataDescription: 'Download a copy of all your data.',
    tabProfile: 'Profile',
    tabInvite: 'Invite friends',
    tabNotifications: 'Notifications',
    tabPrivacy: 'Privacy & Data',
    tabInfo: 'About',
    nameLabel: 'Name',
    namePlaceholder: 'Your name',
    emailLabel: 'Email',
    verified: 'Verified',
    notVerified: 'Not verified',
    saveChanges: 'Save changes',
    saveChangesSuccess: 'Changes saved successfully',
    saveError: 'Error saving',
    languageUpdated: 'Language updated',
    pushOn: 'On',
    pushOff: 'Off',
    pushDescription: 'Receive push notifications directly in the browser',
    pushUnsupported: 'Your browser does not support push notifications or they are blocked',
    emailDescription: 'Receive updates via email',
    inAppDescription: 'Notifications inside the application',
    minAlertLevel: 'Minimum alert level',
    levelGood: 'Good',
    levelRare: 'Rare',
    levelExceptional: 'Exceptional',
    levelHelp: 'You will receive alerts only for conditions at or above this level',
    notifPrefsSavedSoon: 'Notification preferences will be saved soon',
    privacyAndData: 'Privacy & Data',
    exportMyData: 'Export my data',
    exportMyDataDescription: 'Download a copy of all your data (GDPR)',
    exportButton: 'Export data',
    exportComingSoon: 'Feature coming soon. Your data will be exportable shortly.',
    deleteAccountTitle: 'Delete account',
    deleteAccountDescription: 'This action is permanent and cannot be undone',
    deleteDialogTitle: 'Delete account',
    deleteDialogMessage: 'Are you sure? This action cannot be undone. All your data will be deleted.',
    deleteTypedConfirm: 'DELETE',
    deleteError: 'Error during deletion',
    info: 'About',
    appVersion: 'App version',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    support: 'Support',
    inviteTitle: 'Invite friends',
    inviteSubtitle: 'Invite 5 friends and get 5 months of Pro for free!',
    yourInviteLink: 'Your invite link',
    copy: 'Copy',
    copied: 'Copied!',
    linkCopiedToast: 'Link copied!',
    copyErrorToast: 'Copy failed',
    share: 'Share',
    shareAria: 'Share',
    progressLabel: 'invites to unlock 5 months Pro',
    monthsEarnedPrefix: 'You\'ve earned',
    monthsEarnedSuffix: 'months of Pro',
    proActiveUntil: 'Pro active until',
    invitedFriends: 'Invited friends',
    fallbackUserLabel: 'User',
    noInvitesYet: "You haven't invited anyone yet. Share your link!",
    shareTextTemplate: 'Try FlyMind, the AI flight consultant! Sign up with my link and save:',
  },
  landing: {
    hero: {
      title: 'Never overpay for a flight',
      titlePrefix: 'The AI that tells you ',
      titleHighlight: 'when to book',
      subtitle: 'Monitor flight prices, get smart notifications and book at the best price',
      cta: 'Start for free',
      secondaryCta: 'See how it works',
      downloadOn: 'Download on',
      availableOn: 'Get it on',
      appStoreAria: 'Download on App Store',
      googlePlayAria: 'Get it on Google Play',
    },
    howItWorks: {
      title: 'How it works',
      subtitle: 'Three simple steps to start saving',
      step1Title: 'Choose your route',
      step1Description: 'Enter departure airport, destination and dates',
      step2Title: 'We monitor prices',
      step2Description: 'We check prices every 6 hours and compare them with historical averages',
      step3Title: 'Get AI advice',
      step3Description: 'We alert you when it\'s the right time to book, with smart analysis',
    },
    features: {
      heading: 'The tool you were missing',
      smartNotifTitle: 'Smart Notifications',
      smartNotifDesc: '3 types with colored badges so you never miss a deal',
      badgeGood: 'Good price',
      badgeRare: 'Rare price',
      badgeExceptional: 'Exceptional',
      predictiveTitle: 'Predictive AI Analysis',
      predictiveDesc: 'Forecasts based on historical trends to anticipate price moves',
      thresholdsTitle: 'Custom Thresholds',
      thresholdsDesc: 'Set your maximum budget and get alerts only when it counts',
      multichannelTitle: 'Multi-channel',
      multichannelDesc: 'Email, push and in-app notifications so you never miss a deal',
      voiceTitle: 'Voice Compilation',
      voiceDesc: 'Create sentinels by speaking, for when your hands are busy',
      chatTitle: 'Chat with FlyMind Agent',
      chatDesc: 'Ask for advice and manage your searches by chatting with your AI agent',
    },
    pricing: {
      title: 'Simple and transparent pricing',
      subtitle: 'Choose the plan that best suits your needs.',
      heading: 'Choose the plan that best suits your needs',
      mostPopular: 'Most popular',
      forever: 'forever',
      perMonth: 'per month',
      freeName: 'Free',
      proName: 'Pro',
      unlimitedName: 'Unlimited',
      ctaFree: 'Start for free',
      ctaPro: 'Choose Pro',
      ctaUnlimited: 'Choose Unlimited',
    },
    notifications: {
      heading: 'Flymind notifications',
      labelGood: 'GOOD PRICE',
      labelRare: 'RARE PRICE',
      labelExceptional: 'EXCEPTIONAL DEAL',
      priceInLastMonths: 'Price is {delta} of the last 6 months',
      deltaGood: '12% lower',
      deltaRare: '22% lower',
      deltaExceptional: '5% lower',
      ctaGood: 'Keep an eye on it',
      ctaRare: 'Consider booking',
      ctaExceptional: 'We recommend booking now',
    },
    faq: {
      title: 'Frequently asked questions',
      subtitle: 'Everything you need to know about FlyMind',
      q1: 'How does FlyMind work?',
      a1: 'FlyMind automatically monitors flight prices on the routes you choose. Our AI analyses price history and alerts you when it finds a great deal, suggesting the best time to book.',
      q2: 'Is it really free?',
      a2: 'Yes! The Free plan lets you activate 1 sentinel for free, forever. For more sentinels and AI features, you can switch to Pro (€0.99/month) or Unlimited (€4.99/month) at any time.',
      q3: 'How are prices analysed?',
      a3: 'We check flight prices several times a day and compare them with the historical average of recent months. Our AI identifies patterns and trends to predict whether the price will go up or down, helping you book at the right time.',
      q4: 'Can I monitor multiple routes?',
      a4: 'With Free you have 1 sentinel. With Pro (€0.99/month) you get 5 with AI search and predictive alerts. With Unlimited (€4.99/month) you get unlimited sentinels and the AI voice assistant.',
      q5: 'How do I receive notifications?',
      a5: 'You receive notifications via email, browser push and in-app. You can choose which channels to enable from your account settings.',
      q6: 'Is my data safe?',
      a6: 'Absolutely. Your data is encrypted and stored on secure servers in the European Union. We never share your information with third parties. You can delete your account and all data at any time.',
    },
    testimonials: {
      title: 'What our users say',
    },
    finalCta: {
      title: 'Start saving on your flights',
      subtitle: 'Sign up free in 30 seconds',
      followUs: 'Follow us on',
    },
    footer: {
      description: 'FlyMind - Your smart assistant for saving on flights.',
      product: 'Product',
      company: 'Company',
      legal: 'Legal',
      terms: 'Terms of service',
      privacy: 'Privacy policy',
      cookies: 'Cookie policy',
      about: 'About us',
      blog: 'Blog',
      contact: 'Contact us',
      allRightsReserved: 'All rights reserved.',
    },
  },
  contact: {
    title: 'Contact us',
    subtitle: 'Have a question or suggestion? Write to us and we\'ll get back to you soon.',
    nameLabel: 'Name',
    namePlaceholder: 'Your name',
    emailLabel: 'Email',
    emailPlaceholder: 'your@email.com',
    messageLabel: 'Message',
    messagePlaceholder: 'Write your message here...',
    send: 'Send message',
    sending: 'Sending...',
    sentSuccess: 'Message sent!',
    sendError: 'Error sending message. Please try again.',
    errNameRequired: 'Name is required',
    errEmailInvalid: 'Valid email required',
    errMessageMin: 'Message must be at least 10 characters',
    followSocial: 'Follow us on Instagram and Telegram',
  },
  errors: {
    generic: 'An error occurred. Please try again later.',
    notFound: 'Page not found.',
    notFoundDescription: 'The page you are looking for does not exist or has been moved.',
    unauthorized: 'Unauthorized access. Please log in.',
    forbidden: 'You do not have permission to access this resource.',
    validation: 'The data entered is not valid. Please check the highlighted fields.',
    network: 'Connection error. Please check your internet connection.',
    serverError: 'Server error. Please try again later.',
    sessionExpired: 'Your session has expired. Please log in again.',
    rateLimited: 'Too many requests. Please wait a few minutes.',
    emailTaken: 'This email is already registered.',
    invalidCredentials: 'Invalid email or password.',
    weakPassword: 'The password does not meet the minimum requirements.',
    monitorLimitReached: 'You have reached the monitor limit for your plan.',
    paymentFailed: 'Payment failed. Please try again.',
    subscriptionError: 'Error managing subscription.',
    airportNotFound: 'Airport not found.',
    sameAirport: 'Departure and arrival airports must be different.',
    pastDate: 'The selected date is in the past.',
    returnBeforeDeparture: 'The return date must be after the departure date.',
    goBackHome: 'Go back home',
  },
  monitorDetail: {
    bestPrice: 'Best price',
    notYetAvailable: 'Not yet available',
    total: 'total',
    perPerson: 'per person',
    passengers: 'passengers',
    belowAverage: 'below historical average',
    aboveAverage: 'above historical average',
    last30Days: 'last 30 days',
    yourBudget: 'Your budget',
    operatedBy: 'Operated by',
    bookOn: 'You\'ll book on',
    updatedAt: 'updated',
    loadingFares: 'Looking for the best fares…',
    availableOffers: 'Available offers',
    noFares: 'No fares available right now. Try again later.',
    direct: 'Direct',
    stop: 'stop',
    stops: 'stops',
    bookButton: 'Book on',
    site: 'site',
    noBookingDate: 'The departure date has passed.',
    pax: 'pax',
    aiAnalysis: 'AI Analysis',
    confidenceLevel: 'Confidence level',
    estimatedSavings: 'Estimated savings',
    notFound: 'Monitor not found',
    notFoundDescription: 'The monitor you are looking for does not exist or has been deleted.',
    backToDashboard: 'Back to dashboard',
    notifications: 'Notifications',
    notificationsActive: 'On',
    notificationsPaused: 'Paused',
    notificationsTooltip: 'When off, prices keep being tracked but you receive no email/push',
    checksPerformed: 'Checks performed',
    share: 'Share',
    monitorDetails: 'Monitor details',
    createdOn: 'Created on',
    notSet: 'Not set',
    confirmDeletion: 'Confirm deletion',
    belowShort: 'below',
    aboveShort: 'above',
    searchOffersError: 'Error searching offers',
    airline: 'Airline',
    aiBelowSuffix: 'below the average of the last 3 months. This is a good time to book.',
    aiAboveSuffix: 'above average. We recommend waiting for a price drop.',
    aiPriceIsAt: 'The price is',
    aiTrendUp: 'We predict the price will rise in the coming weeks.',
    aiTrendDown: 'We predict the price will drop in the coming weeks.',
  },
  push: {
    title: 'Enable push notifications',
    description: 'Get instant alerts when we find a deal for you',
    enable: 'Enable',
    notNow: 'Not now',
    blockedTitle: 'Notifications blocked by the browser',
    blockedDescription: 'You denied permission in the past and the browser no longer prompts. To re-enable:',
    blockedStep1: 'Click the 🔒 icon to the left of the address bar (or the lock)',
    blockedStep2: 'Find the Notifications setting and switch it to Allow',
    blockedStep3: 'Reload the page and click "Enable" again',
    reload: 'Reload page',
    hide: 'Hide',
  },
  errorBoundary: {
    title: 'Something went wrong',
    description: 'We have logged the error. Please reload the page; if the problem persists, contact us at',
    reload: 'Reload',
    backToHome: 'Back to home',
    detail: 'Detail',
  },
  pricing: {
    free: {
      monitor: '1 monitor',
      notifications: 'Email and push notifications',
      history: 'Price history',
    },
    pro: {
      monitors: '5 monitors',
      notifications: 'Email and push notifications',
      history: 'Price history',
      predictive: 'Predictive alerts',
      aiCompile: 'AI compilation',
    },
    unlimited: {
      monitors: 'Unlimited monitors',
      notifications: 'Email and push notifications',
      history: 'Price history',
      predictive: 'Predictive alerts',
      aiCompile: 'AI compilation',
      voiceAgent: 'AI voice agent',
    },
  },
  share: {
    copy: 'Copy message',
    copied: 'Copied!',
    title: 'Share this deal',
    foundFlight: 'I found a flight',
    flightOfferSubject: 'Flight deal',
    belowAverage: 'below average.',
    discoverMore: 'Find out more on FlyMind.',
  },
  airportSearch: {
    placeholder: 'Search airport...',
    removeSelection: 'Remove selection',
    keepTyping: 'Keep typing to refine the search...',
    noResults: 'No airports found',
    regionAll: 'All',
    regionEurope: 'Europe',
    regionNorthAmerica: 'North Am.',
    regionSouthAmerica: 'South Am.',
    regionAsia: 'Asia',
    regionAfrica: 'Africa',
    regionOceania: 'Oceania',
    regionMiddleEast: 'Middle East',
  },
  invite: {
    bookAtRightTime: 'Book at the right time and save',
    smartNotifications: 'Smart Notifications',
    notificationsDescription: 'Email, push and in-app notifications so you never miss a deal',
    landingTitle: "You've been invited to FlyMind!",
    benefitMonitor: 'Monitor flight prices in real time',
    benefitAlerts: 'Get smart AI-powered alerts',
    benefitBook: 'Book at the right time and save',
    cta: 'Sign up for free',
    footerTagline: 'FlyMind - Your AI flight consultant',
  },
  onboarding: {
    stepOf: 'Step {step} of {total}',
    chooseRoute: 'Choose your route',
    chooseRouteSubtitle: 'Enter departure and destination airports',
    datesAndBudget: 'Dates and Budget',
    datesAndBudgetSubtitle: 'When do you want to leave and what is your budget?',
    fixedDates: 'Fixed dates',
    flexiblePeriod: 'Flexible period',
    periodStart: 'Period start',
    periodEnd: 'Period end',
    flexibleHint: 'We will look for the best price in this range (max 30 days)',
    stayDurationLabel: 'Stay duration (optional)',
    minDays: 'Minimum days',
    maxDays: 'Maximum days',
    daysExample3: 'e.g. 3',
    daysExample7: 'e.g. 7',
    flightPreferences: 'Flight preferences',
    classLabel: 'Class',
    classEconomy: 'Economy',
    classPremiumEconomy: 'Premium Economy',
    classBusiness: 'Business',
    classFirst: 'First',
    passengers: 'Passengers',
    summary: 'Summary',
    summarySubtitle: 'Verify the details of your monitor',
    type: 'Type',
    flexibleMonitoring: 'Flexible monitoring',
    period: 'Period',
    stay: 'Stay',
    days: 'days',
    minPrefix: 'Min',
    maxPrefix: 'Max',
    aiAdvice: 'Our AI will analyse historical prices and tell you when it is best to book.',
    backToDashboard: 'Back to dashboard',
    budgetExample: 'e.g. 150',
    errSelectDeparture: 'Select a departure airport',
    errSelectArrival: 'Select an arrival airport',
    errSelectDepartureDate: 'Select a departure date',
    errSelectStartDate: 'Select the period start date',
    errSelectEndDate: 'Select the period end date',
    errEndAfterStart: 'The end of the period must be after the start',
    errMaxPeriod: 'The period cannot exceed 30 days',
    errStayMinMax: 'The minimum must be less than or equal to the maximum',
    errBudgetPositive: 'Budget must be a positive number',
  },
  accountDeleted: {
    errorMissingToken: 'Invalid confirmation link.',
    errorInvalidToken: 'The confirmation link has expired or is invalid.',
    errorGeneric: 'An error occurred during deletion.',
    errorTitle: 'Error',
    backToLogin: 'Back to login',
    title: 'Account deleted',
    description: 'Your account and all associated data have been permanently deleted.',
    successMessage: 'Thanks for using FlyMind. We are sorry to see you go.',
    processedMessage: 'Your request has been processed.',
    backToHome: 'Back to home',
  },
  bookingRedirect: {
    tagline: 'Your AI flight consultant',
    checking: 'Checking availability...',
    invalidTitle: 'Invalid link',
    invalidDescription: 'The booking link is missing the required parameters.',
    backToDashboard: 'Back to Dashboard',
    chooseWhere: 'Choose where to book this flight:',
    bestPriceSearch: 'Best Price Search',
    poweredByAviasales: 'Powered by Aviasales',
    pricesMayVary: 'Prices may vary. The displayed price was verified at the time of search.',
    pricesChangeQuickly: 'Flight prices change quickly. Go back to the dashboard to search for new deals.',
  },
  voice: {
    inputPlaceholder: 'Ex: Flight Rome Ibiza from June 1 to 7, economy, 2 people',
    howProceed: 'How would you like to proceed?',
    chooseMode: 'Choose the mode to create the monitor',
    aiCompile: 'AI compilation',
    aiCompileDesc: 'Speak, verify the result and confirm',
    aiAgent: 'AI voice agent',
    aiAgentDesc: 'Two-way conversation with the assistant',
    proFunction: 'Pro feature',
    proFunctionDesc: 'AI compilation is available from the Pro plan. Upgrade to Pro to create monitors with your voice.',
    discoverPlans: 'See plans',
    close: 'Close',
    describeFlight: 'Describe the flight you are looking for and the AI will fill in the monitor for you.',
    speakNow: 'Speak now',
    preferWriting: 'Prefer to write?',
    compile: 'Compile with AI',
    preferSpeaking: 'Prefer to speak?',
    listening: 'Listening...',
    autoStops: 'It stops automatically when you stop speaking',
    interrupt: 'Stop',
    analyzing: 'Analyzing...',
    verifyDetails: 'Verify the details',
    create: 'Create monitor',
    retry: 'Try again',
    creating: 'Creating monitor...',
    created: 'Monitor created!',
    goToMonitors: 'Go to monitors',
    newSearch: 'New search',
    errorAnalysis: 'Error during analysis',
    errorCreation: 'Error during creation',
    didntHearAirports: 'I did not understand the airports. Try again specifying departure and destination.',
    didntHearDates: 'I did not understand the dates. Try again specifying when you want to leave.',
    didntHearAnything: 'I did not hear anything. Try again or use text input.',
    connectionError: 'Connection error with the voice assistant.',
    mustAuth: 'You must be authenticated.',
    micDenied: 'Microphone permission denied. Enable the microphone in your browser settings.',
    startError: 'Error during start.',
    startAgent: 'Start voice agent',
    connecting: 'Connecting...',
    connectingShort: 'Connecting...',
    endConversation: 'End conversation',
    assistantSpeaking: 'The assistant is speaking...',
    imListening: 'Listening... go ahead!',
    agentDescription: 'Talk to our AI assistant. It will ask you questions and create the monitor for you.',
    fieldDeparture: 'Departure',
    fieldDestination: 'Destination',
    fieldType: 'Type',
    fieldDepartureDate: 'Departure date',
    fieldReturnDate: 'Return date',
    fieldOneWay: 'One way',
    fieldPeriodFrom: 'Period from',
    fieldPeriodTo: 'Period to',
    fieldBudget: 'Budget',
    fieldPassengers: 'Passengers',
    fieldClass: 'Class',
    fieldStayMin: 'Min stay',
    fieldStayMax: 'Max stay',
    classEconomy: 'Economy',
    classBusiness: 'Business',
    classFirst: 'First',
    classPremiumEconomy: 'Premium Economy',
    typeFlexible: 'Flexible',
    typeFixed: 'Fixed dates',
    days: 'days',
    yes: 'Yes',
    no: 'No',
  },
  confirm: {
    cancel: 'Cancel',
    confirm: 'Confirm',
    typePrefix: 'Type',
    typeSuffix: 'to confirm',
  },
  admin: {
    common: {
      adminBadge: 'Admin',
      superAdminBadge: 'Super Admin',
      details: 'Details',
      saving: 'Saving...',
      pleaseWait: 'Please wait...',
      previous: 'Previous',
      next: 'Next',
      pageOf: 'Page {page} of {total}',
      resultsCount: '{count} results',
      planFree: 'Free',
      planPremium: 'Pro',
      planUnlimited: 'Unlimited',
      roleUser: 'User',
      roleAdmin: 'Admin',
      roleSuperAdmin: 'Super Admin',
      statusActive: 'Active',
      statusBanned: 'Banned',
      subStatusActive: 'Active',
      subStatusPastDue: 'Past due',
      subStatusCancelled: 'Cancelled',
      subStatusTrialing: 'Trialing',
      subStatusIncomplete: 'Incomplete',
      subStatusIncompleteExpired: 'Expired',
      subStatusUnpaid: 'Unpaid',
      subStatusPaused: 'Paused',
      loadingError: 'Error loading data',
      retry: 'Try again',
      yesShort: 'Yes',
      noShort: 'No',
      dash: '-',
    },
    layout: {
      backToApp: 'Back to app',
      logout: 'Log out',
      navDashboard: 'Dashboard',
      navUsers: 'Users',
      navPayments: 'Payments',
      navErrors: 'Errors',
      navDiscounts: 'Discounts',
      pageDashboard: 'Dashboard',
      pageUsers: 'Users',
      pagePayments: 'Payments',
      pageErrors: 'Errors',
      pageDiscounts: 'Discounts',
      pageFallback: 'Admin',
    },
    dashboard: {
      title: 'Admin Dashboard',
      subtitle: 'Overview of key metrics',
      totalUsers: 'Total users',
      activeUsers: 'Active users',
      activeMonitors: 'Active monitors',
      mrr: 'MRR',
      conversionRate: 'Conversion rate',
      newUsersWeek: 'New users (week)',
      subscriptionsByPlan: 'Subscriptions by plan',
      noActiveSubscription: 'No active subscriptions',
      newUsers: 'New users',
      thisWeek: 'This week',
      thisMonth: 'This month',
    },
    users: {
      title: 'User Management',
      totalUsers: '{count} total users',
      searchPlaceholder: 'Search by email or name...',
      filterAllPlans: 'All plans',
      filterAllRoles: 'All roles',
      filterAllStatuses: 'All statuses',
      colName: 'Name',
      colEmail: 'Email',
      colPlan: 'Plan',
      colRole: 'Role',
      colStatus: 'Status',
      colCreated: 'Registered',
      colActions: 'Actions',
      ban: 'Ban',
      unban: 'Unban',
      banUser: 'Ban user',
      unbanUser: 'Unban user',
      confirmBan: 'Are you sure you want to ban this user?',
      confirmUnban: 'Are you sure you want to unban this user?',
      emptyTitle: 'No users found',
      emptyDescription: 'Try adjusting your search filters.',
      detailTitle: 'User Detail',
      backToList: 'Back to user list',
      userNotFound: 'User not found.',
      loadError: 'Unable to load user data.',
      profileSection: 'User Profile',
      fieldName: 'Name',
      fieldEmail: 'Email',
      fieldPlan: 'Plan',
      fieldRole: 'Role',
      fieldStatus: 'Status',
      fieldEmailVerified: 'Email Verified',
      fieldRegistered: 'Registered on',
      fieldUpdated: 'Last updated',
      fieldLanguage: 'Language',
      activeMonitors: 'Active monitors',
      totalNotifications: 'Total notifications',
      subscriptionSection: 'Subscription',
      subPeriod: 'Period',
      actionsSection: 'Actions',
      changePlan: 'Change plan',
      changeRole: 'Change role',
      userStatus: 'User status',
      planFreeOption: 'Free',
      planPremiumOption: 'Premium',
      planUnlimitedOption: 'Unlimited',
    },
    payments: {
      title: 'Payment Management',
      activeCount: '{count} active subscriptions',
      statsLoadError: 'Unable to load statistics.',
      subsLoadError: 'Unable to load subscriptions.',
      mrr: 'MRR',
      activeSubscriptions: 'Active subscriptions',
      churnRate: 'Churn rate',
      annualRevenue: 'Annual revenue',
      filterAllPlans: 'All plans',
      filterAllStatuses: 'All statuses',
      filterStatusActive: 'Active',
      filterStatusCancelled: 'Cancelled',
      filterStatusPastDue: 'Past due',
      filterStatusTrialing: 'Trialing',
      filterStatusUnpaid: 'Unpaid',
      filterStatusPaused: 'Paused',
      colUser: 'User',
      colPlan: 'Plan',
      colStatus: 'Status',
      colPeriodStart: 'Period start',
      colPeriodEnd: 'Period end',
      colActions: 'Actions',
      refund: 'Refund',
      emptyTitle: 'No subscriptions found',
      emptyDescription: 'Try adjusting your search filters.',
      refundConfirmTitle: 'Confirm refund',
      refundConfirmPrefix: 'Are you sure you want to refund the subscription of',
      refundConfirmPlanWord: 'plan',
      refundConfirmSuffix: 'This action cannot be undone.',
      refundError: 'Refund error. Check the console for details.',
    },
    errors: {
      title: 'Error Monitoring',
      foundCount: '{count} errors found',
      cleanOldLogs: 'Clean old logs',
      stats7d: 'Errors (7 days)',
      stats30d: 'Errors (30 days)',
      statsCritical7d: 'Critical (7 days)',
      statsErrors7d: 'Errors (7 days)',
      filterAllSources: 'All sources',
      filterAllSeverities: 'All severities',
      severityCritical: 'Critical',
      severityError: 'Error',
      severityWarning: 'Warning',
      severityInfo: 'Info',
      sourceApi: 'API',
      sourceWorker: 'Worker',
      sourceWebhook: 'Webhook',
      sourceCron: 'Cron',
      sourceAuth: 'Auth',
      dateFromLabel: 'From:',
      dateToLabel: 'To:',
      colSeverity: 'Severity',
      colSource: 'Source',
      colMessage: 'Message',
      colDate: 'Date',
      metadataLabel: 'Metadata:',
      noStackTrace: 'No stack trace available.',
      emptyTitle: 'No errors found',
      emptyDescription: 'There are no error logs matching the selected filters.',
      cleanupTitle: 'Clean old logs',
      cleanupMessage: 'This action will delete all error logs older than 30 days. The operation cannot be undone.',
      cleanupConfirm: 'Delete',
    },
    discounts: {
      title: 'Discount Codes',
      totalCount: '{count} total codes',
      newCode: 'New code',
      colCode: 'Code',
      colDiscount: 'Discount',
      colUses: 'Uses',
      colValidity: 'Validity',
      colStatus: 'Status',
      colActions: 'Actions',
      statusActive: 'Active',
      statusInactive: 'Disabled',
      statusExpired: 'Expired',
      edit: 'Edit',
      activate: 'Activate',
      deactivate: 'Disable',
      delete: 'Delete',
      emptyTitle: 'No discount codes',
      emptyDescription: 'Create your first discount code to get started.',
      modalTitleEdit: 'Edit discount code',
      modalTitleNew: 'New discount code',
      fieldCode: 'Code',
      codePlaceholder: 'e.g. PROMO2026',
      fieldDescription: 'Description',
      descriptionPlaceholder: 'Optional description',
      fieldDiscountPercent: 'Discount percentage',
      fieldMaxUses: 'Maximum uses',
      maxUsesPlaceholder: 'Unlimited',
      fieldValidFrom: 'Valid from',
      fieldValidUntil: 'Valid until',
      fieldApplicablePlans: 'Applicable plans',
      planPro: 'Pro',
      planUnlimited: 'Unlimited',
      cancel: 'Cancel',
      saveChanges: 'Save changes',
      createCode: 'Create code',
      errCodeRequired: 'Code is required',
      errPercentRange: 'Percentage must be between 1 and 100',
      errDatesRequired: 'Validity dates are required',
      errSaveGeneric: 'Error saving',
      deleteTitle: 'Disable discount code',
      deleteMessagePrefix: 'Are you sure you want to disable code',
      deleteMessageSuffix: 'The code will no longer be usable by users.',
      deleteConfirm: 'Disable',
    },
  },
} as const;
